// FaceLock Scan's telemetry: what the scan did on a real device, so a failure
// can be read in the server log (msg "facelock_scan_telemetry") without the
// owner taking screenshots. Sent: every model start (delegate, time, each
// try), every caught error (message, the stack's first 500 characters), every
// restart of a part, each part's start and finish, every picture kept, and,
// in the test mode (?scandebug=1) or while one instruction has been up over
// 8 s, the live numbers every 2 s (for the full body: which way the body model
// reads the person and each cue behind it, so "the back never passes" can be
// answered from the log). Every event carries `build` (lib/build-stamp.ts):
// which bundle the device was running. Numbers and short words only: never a
// picture, never face landmarks.
//
// Batched: at most one request per TELEMETRY.flushMs (the server takes one per
// 2 s per account) and TELEMETRY.maxBytes per request (the server's limit is
// 8 KB). Fire-and-forget (fetch keepalive, so the last batch survives the
// scan closing); a failure is ignored, and nothing is sent without a sign-in
// (the phone a computer handed the scan to sends with its link instead:
// handoffTelemetrySender). The page being left (`leave`) goes at once with
// flushNow: the server keeps a lane of its own for a request carrying it, one
// per 2 s, so every request carrying a `leave` (flushNow's or a paced one)
// keeps TELEMETRY.flushNowMs from the last one; a `leave` that comes sooner
// goes when its lane opens, if the page is still there.
// The server's windows are the account's (the phone's: its link's), not one
// scan's: every telemetry on the page with the same sender keeps the same two
// paces (the open scan's own, the create dialog's `store_fail`, a scan closed
// and opened again), so one never sends inside another's 2 s.
// Only fetch: a beacon cannot carry the sign-in header.

import type { ScanClip } from "@/shared/face-qc/index.mjs";
import { BUILD_STAMP } from "@/lib/build-stamp";
import type { Snapshot } from "./engine";
import { deviceKind } from "./platform";

export const TELEMETRY_URL = "/api/web/facelock/scan-telemetry";
/** The phone's (no sign-in there): the same body, with the phone link's X-Scan-Handoff header. */
export const HANDOFF_TELEMETRY_URL = "/api/web/facelock/scan-handoff/telemetry";
export const TELEMETRY = {
  /** One request at most this often (the server allows one per 2 s). */
  flushMs: 2200,
  /**
   * One request carrying a `leave` at most this often, flushNow's or a paced one (the server's lane for them takes
   * one per 2 s; the same margin as flushMs). flushNow does not wait for the paced slot.
   */
  flushNowMs: 2200,
  /** One request's body at most this big (the server refuses over 8 KB). */
  maxBytes: 7800,
  /** Events waiting at most (the oldest go first). */
  maxQueue: 120,
  /** A string value's longest, and a stack's. */
  strMax: 200,
  stackMax: 500,
  /** Keys per event. */
  maxKeys: 48,
} as const;

export type TelemetryValue = number | string | boolean | null;

export interface TelemetryEvent {
  event: string;
  part: ScanClip | null;
  bucket: string | null;
  /** ms since the scan opened. */
  t: number;
  data: Record<string, TelemetryValue>;
}

export interface TelemetryDevice {
  kind: string;
  ua: string;
  w: number;
  h: number;
  dpr: number;
}

export interface TelemetryPayload {
  v: 1;
  session: string;
  device: TelemetryDevice;
  events: TelemetryEvent[];
}

export interface TrackOptions {
  part?: ScanClip | null;
  bucket?: string | null;
  data?: Record<string, unknown>;
}

export interface ScanTelemetry {
  readonly session: string;
  track(event: string, o?: TrackOptions): void;
  /** A caught error: its message and the first 500 characters of its stack. */
  error(err: unknown, o?: TrackOptions & { phase?: string }): void;
  /** Send what is waiting now if the pace allows (else at the next slot). */
  flush(): void;
  /**
   * Send what is waiting NOW, whatever the pace (the page is being left): only
   * a request carrying a `leave`, one at most per TELEMETRY.flushNowMs (else
   * it goes when that time is up), and the next paced request is not held
   * back by it.
   */
  flushNow(): void;
  /** The scan closed: what is waiting still goes at the next slot. */
  dispose(): void;
}

const KEY = /^[A-Za-z0-9_]{1,32}$/;
const EVENT = /^[a-z][a-z0-9_]{0,31}$/;

const round = (v: number): number => (Number.isInteger(v) ? v : Math.round(v * 1000) / 1000);

function value(k: string, v: unknown): TelemetryValue | undefined {
  if (v === null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? round(v) : null;
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v.slice(0, k === "stack" ? TELEMETRY.stackMax : TELEMETRY.strMax);
  if (Array.isArray(v) && v.every((x) => x === null || ["number", "string", "boolean"].includes(typeof x))) {
    return v.map((x) => (typeof x === "number" ? round(x) : String(x))).join(",").slice(0, TELEMETRY.strMax);
  }
  return undefined;
}

/** Flat numbers and short words only: nested objects one level deep become `outer_inner`; anything else is dropped. */
export function cleanData(data: Record<string, unknown> | undefined): Record<string, TelemetryValue> {
  const out: Record<string, TelemetryValue> = {};
  let n = 0;
  const put = (k: string, v: unknown) => {
    if (n >= TELEMETRY.maxKeys || !KEY.test(k)) return;
    const c = value(k, v);
    if (c === undefined) return;
    out[k] = c;
    n += 1;
  };
  for (const [k, v] of Object.entries(data ?? {})) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) put(`${k}_${k2}`, v2);
    } else {
      put(k, v);
    }
  }
  return out;
}

/** Does it carry the page being left? (The server judges such a request on a lane of its own.) */
const hasLeave = (events: readonly TelemetryEvent[]): boolean => events.some((e) => e.event === "leave");

const bytes = (s: string): number => (typeof TextEncoder !== "undefined" ? new TextEncoder().encode(s).length : s.length * 3);

/**
 * The next request: as many waiting events as fit in `maxBytes` (oldest
 * first). An event too big on its own is dropped. Returns the payload (null
 * when nothing fits) and how many events it used.
 */
export function buildPayload(session: string, device: TelemetryDevice, queue: readonly TelemetryEvent[], maxBytes: number = TELEMETRY.maxBytes): { payload: TelemetryPayload | null; used: number } {
  const events: TelemetryEvent[] = [];
  let used = 0;
  for (const ev of queue) {
    const next = { v: 1 as const, session, device, events: [...events, ev] };
    if (bytes(JSON.stringify(next)) <= maxBytes) {
      events.push(ev);
      used += 1;
      continue;
    }
    // Too big even alone: skip it; else it waits for the next request.
    if (!events.length) { used += 1; continue; }
    break;
  }
  return { payload: events.length ? { v: 1, session, device, events } : null, used };
}

/** A live snapshot's numbers (the "snapshot" event): what the test view shows, flat. */
export function snapshotData(s: Snapshot, extra: Record<string, unknown> = {}): Record<string, unknown> {
  const i = s.instruction;
  const words = `${i.kind}${"key" in i ? `:${i.key}` : "bucket" in i ? `:${i.bucket}` : "n" in i ? `:${i.n}` : ""}`;
  const d = s.debug;
  if (!d) return { instruction: words, ...extra };
  return {
    instruction: words,
    gate: d.gate,
    relax: d.relax,
    onTargetS: d.onTargetS,
    tracker: d.tracker,
    yaw: d.yaw,
    pitch: d.pitch,
    pitchFromLevel: d.pitchFromLevel,
    facePct: d.faceSizePct,
    faceHeightPct: d.faceHeightPct,
    faces: d.faces,
    persons: d.persons,
    body: d.body,
    // The full body: front / left / right / back / unknown, and each cue behind it (flattened to facing_*).
    ...(d.facing ? { facing: d.facing } : {}),
    light_mean: d.light?.mean ?? null,
    light_p90: d.light?.p90 ?? null,
    light_clippedPct: d.light?.clipHiPct ?? null,
    light_problems: d.light ? d.light.problems.join(",") : null,
    sharp: d.sharpness,
    sharpRef: d.sharpRef === null ? null : Math.round(d.sharpRef),
    still: d.still,
    stillMs: d.stillMs,
    fps: d.fps,
    detectMs: d.detectMs,
    pixelMs: d.pixelMs,
    workMs: d.workMs,
    level: d.level,
    maxSide: d.maxSide,
    delegate: d.delegate,
    frame: `${d.frameW}x${d.frameH}`,
    ...extra,
  };
}

function authToken(): string {
  try { return localStorage.getItem("auth_token") || ""; } catch { return ""; }
}

export function deviceBrief(): TelemetryDevice {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const win = typeof window !== "undefined" ? window : undefined;
  return {
    kind: deviceKind(),
    ua: String(nav?.userAgent ?? "").slice(0, 160),
    w: Math.round(win?.innerWidth ?? 0),
    h: Math.round(win?.innerHeight ?? 0),
    dpr: Math.round((win?.devicePixelRatio ?? 1) * 100) / 100,
  };
}

/** POST the payload with `headers`, keepalive (it survives the page closing), errors ignored. */
function post(url: string, headers: Record<string, string>, payload: TelemetryPayload): boolean {
  if (typeof fetch !== "function") return false;
  try {
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify(payload),
    }).catch(() => undefined);
  } catch { /* ignored */ }
  return true;
}

/** The default sender: POST with the sign-in, keepalive, errors ignored. False = not sent (no sign-in). */
export function sendTelemetry(payload: TelemetryPayload): boolean {
  const token = authToken();
  if (!token) return false;
  return post(TELEMETRY_URL, { Authorization: `Bearer ${token}` }, payload);
}

/** The phone's sender (pages/FaceLockScanPhone.tsx): the phone link's token instead of a sign-in. False without one. */
export function handoffTelemetrySender(token: string | null): (payload: TelemetryPayload) => boolean {
  return (payload) => (token ? post(HANDOFF_TELEMETRY_URL, { "X-Scan-Handoff": token }, payload) : false);
}

type Sender = (p: TelemetryPayload) => boolean | void;

/** The server's two windows for one sender: when its last paced request went, and its last carrying a `leave`. */
interface Lane { lastSent: number; lastNowAt: number }
const lanes = new WeakMap<Sender, Lane>();
/** The lane every telemetry with this sender shares (the default sender: the signed-in account's). */
function laneFor(send: Sender): Lane {
  let lane = lanes.get(send);
  if (!lane) { lane = { lastSent: -Infinity, lastNowAt: -Infinity }; lanes.set(send, lane); }
  return lane;
}

export function createScanTelemetry(opts: {
  send?: Sender;
  now?: () => number;
  device?: TelemetryDevice;
  session?: string;
} = {}): ScanTelemetry {
  const now = opts.now ?? (() => Date.now());
  const send = opts.send ?? sendTelemetry;
  const session = opts.session ?? Math.random().toString(36).slice(2, 12);
  let device: TelemetryDevice | null = opts.device ?? null;
  const start = now();
  let queue: TelemetryEvent[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  // Shared with every telemetry on the page that has this sender (the server judges them all on one account).
  const lane = laneFor(send);
  // How long since `at` (a clock that went back counts as long ago, so nothing waits on it).
  const since = (at: number) => { const d = now() - at; return d >= 0 ? d : Infinity; };
  // How long until the next paced request may go, and the next carrying a `leave` (the server's lane for those).
  const pacedWait = () => Math.max(0, TELEMETRY.flushMs - since(lane.lastSent));
  const leaveWait = () => Math.max(0, TELEMETRY.flushNowMs - since(lane.lastNowAt));

  const schedule = () => {
    if (timer !== null || !queue.length) return;
    const wait = Math.max(pacedWait(), hasLeave(queue) ? leaveWait() : 0);
    timer = setTimeout(() => { timer = null; flush(); }, wait);
  };

  function flush() {
    if (!queue.length) return;
    if (pacedWait() > 0) { schedule(); return; }
    device ??= deviceBrief();
    const { payload, used } = buildPayload(session, device, queue);
    // A paced request carrying a `leave` is judged on the leave lane too: it waits for that as well.
    if (payload && hasLeave(payload.events) && leaveWait() > 0) { schedule(); return; }
    queue = queue.slice(used);
    if (payload) {
      lane.lastSent = now();
      if (hasLeave(payload.events)) lane.lastNowAt = lane.lastSent;
      let sent: boolean | void = false;
      try { sent = send(payload); } catch { sent = false; }
      // Not signed in: nothing will ever go; keep nothing.
      if (sent === false) queue = [];
    }
    schedule();
  }

  let nowTimer: ReturnType<typeof setTimeout> | null = null;
  function flushNow() {
    if (!hasLeave(queue)) return;
    const wait = leaveWait();
    if (wait > 0) {
      // Not sent to be refused: it goes when the leave lane opens (if the page is still there by then).
      nowTimer ??= setTimeout(() => { nowTimer = null; flushNow(); }, wait);
      return;
    }
    device ??= deviceBrief();
    // Oldest first, as a paced request. When not everything fits: the newest that do (the event that asked for
    // this, the `leave`, is the newest); the older ones wait for the next paced slot.
    const head = buildPayload(session, device, queue);
    let payload = head.payload;
    let rest = queue.slice(head.used);
    if (rest.length) {
      let from = queue.length;
      while (from > 0 && bytes(JSON.stringify({ v: 1, session, device, events: queue.slice(from - 1) })) <= TELEMETRY.maxBytes) from -= 1;
      if (from < queue.length) {
        payload = { v: 1, session, device, events: queue.slice(from) };
        rest = queue.slice(0, from);
      }
    }
    // Only a request carrying a `leave` goes in the leave lane; anything else waits for its paced slot.
    if (!payload || !hasLeave(payload.events)) return;
    queue = rest;
    // Not lastSent: the next paced request keeps its own slot.
    lane.lastNowAt = now();
    let sent: boolean | void = false;
    try { sent = send(payload); } catch { sent = false; }
    if (sent === false) queue = [];
    schedule();
  }

  const track = (event: string, o: TrackOptions = {}) => {
    if (!EVENT.test(event)) return;
    queue.push({
      event,
      part: o.part ?? null,
      bucket: o.bucket ?? null,
      t: Math.max(0, Math.round(now() - start)),
      // The build first, so it is never the key that falls off the end.
      data: cleanData({ build: BUILD_STAMP, ...(o.data ?? {}) }),
    });
    if (queue.length > TELEMETRY.maxQueue) queue = queue.slice(queue.length - TELEMETRY.maxQueue);
    flush();
  };

  return {
    session,
    track,
    error(err, o = {}) {
      const e = err as { name?: string; message?: string; stack?: string; phase?: string } | null;
      track("error", {
        part: o.part,
        bucket: o.bucket,
        data: {
          ...(o.data ?? {}),
          phase: o.phase ?? e?.phase ?? null,
          name: e?.name ?? typeof err,
          message: String(e?.message ?? err).slice(0, TELEMETRY.strMax),
          stack: typeof e?.stack === "string" ? e.stack.slice(0, TELEMETRY.stackMax) : null,
        },
      });
    },
    flush,
    flushNow,
    dispose() {
      flush();
    },
  };
}
